import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency, formatInterval } from '../../utils/formatters';

/**
 * PlanCard Component
 *
 * Displays a single subscription plan with pricing and features.
 * Supports trial selection, plan change, and free plan activation.
 */
const PlanCard = ({
    plan,
    isCurrentPlan,
    isProcessing,
    hasActiveSubscription,
    canStartTrial,
    onSelect,
    onStartTrial,
    currentOwnerCount,
    isDowngradeBlocked
}) => {
    const isFree = plan.isFree || plan.price === 0;

    const handleSelectClick = () => {
        if (!isProcessing && !isCurrentPlan && !isDowngradeBlocked) {
            onSelect(plan);
        }
    };

    const handleTrialClick = () => {
        if (!isProcessing && onStartTrial) {
            onStartTrial(plan);
        }
    };

    // Determine primary button text
    const getButtonText = () => {
        if (isProcessing) return 'Przetwarzanie...';
        if (isCurrentPlan) return 'Aktualny plan';
        if (isDowngradeBlocked) return 'Za dużo klientów';
        if (hasActiveSubscription) return 'Zmień plan';
        if (isFree) return 'Wybierz darmowy';
        return 'Kup teraz';
    };

    // Determine if primary button is disabled
    const isDisabled = isProcessing || isCurrentPlan || isDowngradeBlocked;

    // Show trial button only for paid plans when trial is available
    const showTrialButton = canStartTrial && !isFree && !hasActiveSubscription && !isCurrentPlan;

    return (
        <div
            className={`plan-card ${plan.recommended ? 'recommended' : ''} ${isCurrentPlan ? 'current' : ''} ${isDowngradeBlocked ? 'downgrade-blocked' : ''} ${isFree ? 'free-plan' : ''}`}
        >
            {plan.recommended && (
                <div className="badge recommended-badge">Polecany</div>
            )}
            {isCurrentPlan && (
                <div className="badge current-badge">Obecny</div>
            )}
            {isFree && !isCurrentPlan && (
                <div className="badge free-badge">Darmowy</div>
            )}

            <h2>{plan.name}</h2>

            <div className="price">
                {isFree ? (
                    <span className="amount free">0 zł</span>
                ) : (
                    <>
                        <span className="amount">
                            {formatCurrency(plan.price, plan.currency)}
                        </span>
                        <span className="period">
                            / {formatInterval(plan.period)}
                        </span>
                    </>
                )}
            </div>

            {plan.description && (
                <p className="plan-description">{plan.description}</p>
            )}

            <ul className="features">
                {plan.features.map((feature, index) => (
                    <li key={index}>
                        <span className="check">✔</span>
                        {feature}
                    </li>
                ))}
            </ul>

            {isDowngradeBlocked && (
                <div className="downgrade-warning">
                    Masz {currentOwnerCount} klientów. Ten plan pozwala na maksymalnie {plan.maxOwners}.
                </div>
            )}

            <div className="plan-actions">
                {/* Trial button - shown for paid plans when trial available */}
                {showTrialButton && (
                    <button
                        className="trial-btn"
                        onClick={handleTrialClick}
                        disabled={isProcessing}
                    >
                        {isProcessing ? 'Przetwarzanie...' : 'Wypróbuj 30 dni za darmo'}
                    </button>
                )}

                {/* Primary action button */}
                <button
                    className={`select-btn ${showTrialButton ? 'secondary' : 'primary'}`}
                    onClick={handleSelectClick}
                    disabled={isDisabled}
                >
                    {getButtonText()}
                </button>
            </div>
        </div>
    );
};

PlanCard.propTypes = {
    plan: PropTypes.shape({
        id: PropTypes.string.isRequired,
        code: PropTypes.string,
        priceId: PropTypes.string,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        price: PropTypes.number.isRequired,
        currency: PropTypes.string,
        period: PropTypes.string,
        features: PropTypes.arrayOf(PropTypes.string),
        recommended: PropTypes.bool,
        maxOwners: PropTypes.number,
        isFree: PropTypes.bool
    }).isRequired,
    isCurrentPlan: PropTypes.bool,
    isProcessing: PropTypes.bool,
    hasActiveSubscription: PropTypes.bool,
    canStartTrial: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onStartTrial: PropTypes.func,
    currentOwnerCount: PropTypes.number,
    isDowngradeBlocked: PropTypes.bool
};

PlanCard.defaultProps = {
    isCurrentPlan: false,
    isProcessing: false,
    hasActiveSubscription: false,
    canStartTrial: false,
    onStartTrial: null,
    currentOwnerCount: 0,
    isDowngradeBlocked: false
};

export default PlanCard;
